/**
 * Reading the PROVIDER'S catalog (`GET /api/ai/models`) — the admin add
 * form's suggestions, and nothing else.
 *
 * Since the admin-managed list landed, the catalog no longer feeds any
 * user-facing picker: it tells the /admin page which model ids exist (a
 * `<datalist>`) and what each of them makes, so adding "the new image model"
 * does not require the admin to also remember which half of the list it
 * belongs in. Everything here is pure.
 */
import type { AiModelKind, AiModelListing } from "@kola/shared";

/** USD per million tokens — the unit people actually compare models in. */
const PER_MILLION = 1_000_000;

/**
 * One suggestion line: the model's name, then its id when the name doesn't
 * already contain it, then input/output price per million tokens.
 *
 * A model that quotes NO price shows none rather than "$0.00": free and
 * unpriced look identical at two decimal places, and the difference is a
 * surprise on the invoice.
 */
export function modelOptionLabel(model: AiModelListing): string {
  const parts = [model.name === undefined || model.name === "" ? model.id : model.name];
  if (model.name !== undefined && model.name !== "" && !model.name.includes(model.id)) {
    parts.push(`(${model.id})`);
  }
  const price = priceLabel(model);
  if (price !== null) parts.push(`· ${price}`);
  return parts.join(" ");
}

function priceLabel(model: AiModelListing): string | null {
  const { promptPriceUsd, completionPriceUsd } = model;
  if (promptPriceUsd === undefined && completionPriceUsd === undefined) return null;
  if (promptPriceUsd === 0 && completionPriceUsd === 0) return "free";
  const parts: string[] = [];
  if (promptPriceUsd !== undefined) parts.push(`in ${perMillion(promptPriceUsd)}`);
  if (completionPriceUsd !== undefined) parts.push(`out ${perMillion(completionPriceUsd)}`);
  return `${parts.join(" / ")} per 1M`;
}

function perMillion(usdPerToken: number): string {
  const usd = usdPerToken * PER_MILLION;
  // Sub-cent models are the common case for the cheap tier — two decimals
  // would round a real difference to "$0.00 vs $0.00".
  return usd > 0 && usd < 0.1 ? `$${usd.toFixed(3)}` : `$${usd.toFixed(2)}`;
}

/** Outputs images — by the catalog's word, else by the id's. */
export function isImageModel(model: AiModelListing): boolean {
  if (model.outputModalities !== undefined) return model.outputModalities.includes("image");
  return /image|imagen|banana|seedream|flux|dall-e/i.test(model.id);
}

/**
 * What the catalog says a model id MAKES, or `undefined` when it has never
 * heard of the id. Undefined matters: the add form pre-fills the kind from
 * this and must leave the admin's own choice alone for an id the catalog does
 * not list — which is precisely the brand-new model this list exists to reach.
 */
export function catalogKind(
  catalog: readonly AiModelListing[],
  modelId: string,
): AiModelKind | undefined {
  const entry = catalog.find((model) => model.id === modelId);
  if (entry === undefined) return undefined;
  return isImageModel(entry) ? "image" : "text";
}
